import { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery } from './query';
import { OriginDestinationLogic } from './filters';
import { AirportMarketFilter } from '../../../_integrations/everymundo/airports';
import { tagsFromTravelerInterest } from '../_commons/fares';

export const AirportType = {
  ORIGIN: 'ORIGIN',
  DESTINATION: 'DESTINATION'
};

const getDestinationAirportLogic = ({
  newPrePopulationAirportSetting,
  airportType,
  isTarvelInteresActive
}) => {
  let airportSourceDestinations = 'TRFX';
  let routeSourceDestinations = 'TRFX';
  if (Boolean(newPrePopulationAirportSetting?.airportSourceDestinations)) {
    airportSourceDestinations = newPrePopulationAirportSetting['airportSourceDestinations'];
    delete newPrePopulationAirportSetting['airportSourceDestinations'];
  }
  if (Boolean(newPrePopulationAirportSetting?.routeSourceDestinations)) {
    routeSourceDestinations = newPrePopulationAirportSetting['routeSourceDestinations'];
    delete newPrePopulationAirportSetting['routeSourceDestinations'];
  }
  if (isTarvelInteresActive) {
    return {
      ...newPrePopulationAirportSetting,
      airportSource:
        airportType === 'DESTINATION'
          ? airportSourceDestinations
          : newPrePopulationAirportSetting?.airportSource,
      routeSource:
        airportType === 'DESTINATION'
          ? routeSourceDestinations
          : newPrePopulationAirportSetting?.routeSource
    };
  } else {
    return newPrePopulationAirportSetting;
  }
};

const checkIfInterestsAreEqual = (arr1, arr2) => {
  if (arr1.length !== arr2.length) {
    return false;
  }
  const set1 = new Set(arr1);
  const set2 = new Set(arr2);
  return [...set1].every((item) => set2.has(item));
};

export function useLocations({
  getAirports,
  prePopulation,
  integrationSettings,
  origin,
  destination,
  meta,
  pageContext,
  initialData,
  airportType = AirportType.ORIGIN,
  active = true,
  avoidCall = false,
  travelerInterest = {},
  isTarvelInteresActive = false,
  travelInterestChangeConfirmed = false,
  travelInterestChangeProcessed = false,
  selectDestination,
  dispatch,
  setTravelerInterestSelected,
  clearDestination = () => {},
  travelInterestsAreEqual,
  lastUserAction
}) {
  const tags = tagsFromTravelerInterest(travelerInterest);
  const prevTags = useRef(tags);
  const stableTags = useMemo(() => {
    if (JSON.stringify(prevTags?.current) !== JSON.stringify(tags)) {
      prevTags.current = tags;
    }
    return prevTags.current;
  }, [tags]);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const isActive =
    active &&
    (airportType === AirportType.ORIGIN ||
      origin ||
      (airportType === AirportType.DESTINATION && !origin) ||
      isFirstLoad);

  const getData = () => {
    if (avoidCall) {
      return Promise.resolve([]);
    }
    let dataPromise;
    if (isFirstLoad && initialData) {
      dataPromise = Promise.resolve(initialData);
    } else {
      const routeOption = { airportType };
      if (origin) {
        routeOption.selectedOrigin = origin;
      }
      if (destination) {
        routeOption.selectedDestination = destination;
      }
      let resultAirPort = { ...prePopulation.airport };

      if (prePopulation.airport?.filterOriginAirportsByMarket === true) {
        if (airportType !== AirportType.ORIGIN) {
          delete resultAirPort.filterFields;
        }
      } else if (prePopulation.filterFields !== null && prePopulation.filterFields !== undefined) {
        switch (prePopulation.filterFields.toUpperCase()) {
          case AirportMarketFilter.NONE:
            delete resultAirPort.filterFields;
            break;
          case AirportMarketFilter.ORIGIN:
            if (airportType !== AirportType.ORIGIN) {
              delete resultAirPort.filterFields;
            }
            break;
          case AirportMarketFilter.DESTINATION:
            if (airportType !== AirportType.DESTINATION) {
              delete resultAirPort.filterFields;
            }
            break;
          case AirportMarketFilter.ALL:
          default:
        }
      }
      const newPrePopulationAirportSetting = { ...prePopulation?.airport?.setting };
      if (tags.length > 0) {
        resultAirPort['filterFields'] = [
          {
            name: 'tagId',
            values: tags
          }
        ];
        const destinationAirportLogicParams = getDestinationAirportLogic({
          newPrePopulationAirportSetting,
          airportType,
          isTarvelInteresActive
        });
        resultAirPort['setting'] = {
          ...destinationAirportLogicParams
        };
      } else {
        if (Boolean(newPrePopulationAirportSetting?.airportSourceDestinations)) {
          delete newPrePopulationAirportSetting['airportSourceDestinations'];
        }
        if (Boolean(newPrePopulationAirportSetting?.routeSourceDestinations)) {
          delete newPrePopulationAirportSetting['routeSourceDestinations'];
        }
        resultAirPort['setting'] = {
          ...newPrePopulationAirportSetting
        };
      }

      const marketValue =
        prePopulation.autoSettings?.market ??
        (Array.isArray(prePopulation.markets) ? prePopulation.markets[0] : prePopulation.markets);

      dataPromise = getAirports(
        {
          market: marketValue,
          ...resultAirPort,
          airlineIataCode: prePopulation.airlineIataCode,
          language: prePopulation.languageCode,
          routeOption
        },
        integrationSettings,
        meta,
        pageContext
      );
    }

    return dataPromise
      .then((data) => {
        setIsFirstLoad(false);
        if (
          isTarvelInteresActive &&
          airportType === AirportType.DESTINATION &&
          lastUserAction === 'TRAVELER_CHANGE'
        ) {
          if (selectDestination?.value && selectDestination?.value !== '') {
            const foundSelectedDestinatio = data?.findIndex(
              (element) => element.value === selectDestination?.value
            );
            if (foundSelectedDestinatio === -1) {
              clearDestination();
            } else {
              if (!travelInterestsAreEqual && travelInterestChangeConfirmed) {
                dispatch({ type: 'CHANGE_TRAVEL_INTEREST_CONFIRMATION', payload: false });
                dispatch({
                  type: 'TRAVEL_INTEREST_CHANGE_PROCESSED',
                  payload: !travelInterestChangeProcessed
                });
                setTravelerInterestSelected(travelerInterest);
              }
            }
          } else {
            if (!travelInterestsAreEqual && travelInterestChangeConfirmed) {
              dispatch({ type: 'CHANGE_TRAVEL_INTEREST_CONFIRMATION', payload: false });
              dispatch({
                type: 'TRAVEL_INTEREST_CHANGE_PROCESSED',
                payload: !travelInterestChangeProcessed
              });
              setTravelerInterestSelected(travelerInterest);
            }
          }
        }
        return data;
      })
      .catch((err) => {
        setIsFirstLoad(false);
        throw err;
      });
  };
  return useQuery({ getData, active: isActive }, [
    integrationSettings,
    origin,
    destination,
    prePopulation.airlineIataCode,
    prePopulation.airport,
    prePopulation.languageCode,
    meta,
    stableTags
  ]);
}

export function useOriginDestination({
  dispatch,
  origin,
  destination,
  showOrigin,
  showDestination,
  filter,
  initialOrigins,
  initialDestinations,
  originDestinationLogic,
  userInteraction,
  secondary,
  travelerInterest = {},
  isTarvelInteresActive = false,
  isTravelInteresPrepopulated = false,
  lastUserAction,
  travelInterestChangeConfirmed,
  travelInterestChangeProcessed
}) {
  const [originSelected, setOriginSelected] = useState(origin.value);
  const [destinationSelected, setDestinationSelected] = useState(destination.value);
  const [travelerInterestSelected, setTravelerInterestSelected] = useState(travelerInterest);
  const [updateDestinatios, setUpdateDestinations] = useState(false);
  let destination_ = { ...destination };

  let avoidCall = originDestinationLogic === OriginDestinationLogic.SAME && !userInteraction;
  if (isTarvelInteresActive && originDestinationLogic === OriginDestinationLogic.SAME) {
    if (
      !updateDestinatios &&
      (lastUserAction === 'ORIGIN_CHANGE_NO_CONFIRMED' ||
        lastUserAction === 'TRAVELER_CHANGE' ||
        lastUserAction === 'ORIGIN_CHANGE')
    ) {
      setUpdateDestinations(true);
    }
    if (isTravelInteresPrepopulated && avoidCall) {
      avoidCall = false;
    }
  }
  let parameters = {
    ...filter,
    active: showOrigin,
    airportType: AirportType.ORIGIN,
    initialData: initialOrigins
  };
  if (originDestinationLogic.toLowerCase() !== OriginDestinationLogic.SAME) {
    parameters['destination'] = secondary ? destinationSelected : destination.value;
  }
  const origins = useLocations(parameters);

  const travelInterestsAreEqual = checkIfInterestsAreEqual(
    tagsFromTravelerInterest(travelerInterestSelected),
    tagsFromTravelerInterest(travelerInterest)
  );

  let destinations = useLocations({
    ...filter,
    active: showDestination,
    airportType: AirportType.DESTINATION,
    origin: secondary ? originSelected : origin.value,
    initialData: initialDestinations,
    avoidCall,
    travelerInterest,
    isTarvelInteresActive,
    travelInterestChangeConfirmed,
    travelInterestChangeProcessed,
    selectDestination: destination,
    lastUserAction,
    dispatch,
    travelInterestsAreEqual,
    setTravelerInterestSelected,
    clearDestination: () => {
      if (destinationSelected) {
        setDestinationSelected(undefined);
      }
      dispatch({ type: 'AUTO_CLEAR_DESTINATION' });
      destination_.value = '';
    }
  });
  if (isTarvelInteresActive) {
    if (!isTravelInteresPrepopulated) {
      if (originDestinationLogic === OriginDestinationLogic.SAME && !updateDestinatios) {
        destinations = { ...origins };
      }
    }
  } else {
    if (avoidCall || (destinations.data.length === 0 && !destinations.isLoading)) {
      destinations = { ...origins };
    }
  }

  const clearOrigin = () => {
    if (originSelected) {
      setOriginSelected(undefined);
    }
    dispatch({ type: 'ORIGIN_CLEAR' });
    if (secondary) {
      dispatch({ type: 'ORIGIN_CLEAR_SECONDARY' });
    }
  };

  const clearDestination = () => {
    if (destinationSelected) {
      setDestinationSelected(undefined);
    }
    dispatch({ type: 'DESTINATION_CLEAR' });
    if (secondary) {
      dispatch({ type: 'DESTINATION_CLEAR_SECONDARY' });
    }
  };
  const selectOrigin = ({ value }) => {
    dispatch({ type: 'SET_USER_INTERACTION' });
    dispatch({ type: 'SET_LAST_USER_ACTION_CHANGED', payload: 'ORIGIN_CHANGE_NO_CONFIRMED' });
    if (destinationSelected === value) {
      setDestinationSelected(undefined);
    }
    setOriginSelected(value);
  };
  const selectDestination = ({ value }) => {
    dispatch({ type: 'SET_USER_INTERACTION' });
    dispatch({ type: 'SET_LAST_USER_ACTION_CHANGED', payload: 'DESTINATION_CHANGE_NO_CONFIRMED' });
    setDestinationSelected(value);
  };

  const onChangeOrigin = (newOrigin) => {
    dispatch({ type: 'ORIGIN_CHANGE', payload: newOrigin });
    dispatch({ type: 'SET_LAST_USER_ACTION_CHANGED', payload: 'ORIGIN_CHANGE' });
  };

  const onChangeDestination = (newDestination) => {
    dispatch({
      type: 'DESTINATION_CHANGE',
      payload: newDestination
    });
    dispatch({ type: 'SET_LAST_USER_ACTION_CHANGED', payload: 'DESTINATION_CHANGE' });
  };

  const swapLocations = () => {
    dispatch({ type: 'SWAP_LOCATIONS' });
    dispatch({ type: 'SET_USER_INTERACTION' });
  };

  useEffect(() => {
    if (!userInteraction && origin?.value !== '' && origins.data && origins.data.length > 0) {
      const originExists = origins.data.some((item) => item.value === origin.value);
      if (!originExists) {
        setOriginSelected('');
        origin.value = '';
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origins.data, origin.value, origin]);

  useEffect(() => {
    if (
      !userInteraction &&
      destination?.value !== '' &&
      destinations.data &&
      destinations.data.length > 0
    ) {
      const destinationExists = destinations.data.some((item) => item.value === destination.value);
      if (!destinationExists) {
        setDestinationSelected('');
        destination.value = '';
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinations.data, destination.value, destination]);

  // [values, config]
  return {
    origin: {
      ...origins,
      value: origin.value,
      valueSelect: originSelected,
      selectOrigin,
      onChange: onChangeOrigin,
      onClear: clearOrigin
    },
    destination: {
      ...destinations,
      value: destination_.value,
      valueSelect: destinationSelected,
      selectDestination,
      onChange: onChangeDestination,
      onClear: clearDestination
    },
    swapLocations
  };
}
